import { Body } from '@assembly-js/design-system';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React from 'react';

import { CrmDetailWrapper as DetailWrapper } from 'src/components/CrmDetailWrapper';
import { CustomFieldWrapper } from 'src/features/crm/properties/CustomFieldWrapper';
import {
  CustomFieldValue,
  CustomFieldValueData,
} from 'src/features/crm/types/type';
import { getFieldValidationByType } from 'src/features/crm/utlis/CustomFieldsUtils';
import {
  CustomFieldOption,
  MultiSelectCustomField,
} from 'src/store/clients/types';

interface Props {
  id: string;
  initialFieldValue: CustomFieldValue;
  field: MultiSelectCustomField | CustomFieldOption;
  onFieldValueUpdate: (fieldData: CustomFieldValueData) => void;
  setAsAnchor: boolean;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  onPropertyNameClicked: (event: React.MouseEvent<HTMLElement>) => void;
  // When multi select menu options are (created/edited),
  // it should call save property method to save
  // new/edited options inside the custom field
  onSaveProperty: (
    customField: MultiSelectCustomField | CustomFieldOption,
    deletedOptionKey?: string,
  ) => Promise<void>;
  isEditing: boolean;
  onSetActiveFieldId: (fieldId: string | null) => void;
}

const CustomPropertyRow: React.FC<Props> = ({
  id,
  initialFieldValue,
  field,
  onFieldValueUpdate,
  setAsAnchor,
  setAnchorEl,
  onPropertyNameClicked,
  onSaveProperty,
  isEditing,
  onSetActiveFieldId,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };
  const rowElement = React.useRef<HTMLDivElement | null>(null);
  const [fieldValue, setFieldValue] =
    React.useState<CustomFieldValue>(initialFieldValue);

  React.useEffect(() => {
    if (setAsAnchor && rowElement) {
      setAnchorEl(rowElement.current || null);
    }
  }, []);

  const handleEditToggle = (editing: boolean) => {
    onSetActiveFieldId(editing ? field.id : null);
  };

  React.useEffect(() => {
    setFieldValue(initialFieldValue);
  }, [initialFieldValue]);

  /**
   * This method checks if the emitted value
   * is different from the field current value
   * to perform the update function. Otherwise,
   * there is no sense to update.
   * @param newValue
   */
  const handleUpdateFieldValue = (newValue: string) => {
    const trimmedValue = newValue?.trim();
    if (!trimmedValue && !fieldValue) {
      return;
    }
    if (trimmedValue !== fieldValue) {
      setFieldValue(trimmedValue);
      onFieldValueUpdate({
        fieldId: field.id,
        value: trimmedValue,
      });
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-row gap-2 items-start"
    >
      <div
        className="min-w-[128px] max-w-[128px] cursor-grab active:cursor-grabbing"
        ref={rowElement}
        {...attributes}
        {...listeners}
      >
        <DetailWrapper
          className="py-0 px-0! min-h-8! relative border-transparent hover:border-transparent"
          onClick={onPropertyNameClicked}
        >
          <Body size="sm" className="text-gray-small truncate">
            {field.name}
          </Body>
        </DetailWrapper>
      </div>

      <DetailWrapper className="py-0 px-0! relative border-transparent hover:border-transparent min-h-8! ">
        <CustomFieldWrapper
          field={field}
          isEditing={isEditing}
          onSetIsEditing={handleEditToggle}
          onUpdate={handleUpdateFieldValue}
          fieldValue={fieldValue || ''}
          onSaveProperty={onSaveProperty}
          validate={(val) => getFieldValidationByType(field.type, val)}
          onFieldValueUpdate={onFieldValueUpdate}
        />
      </DetailWrapper>
    </div>
  );
};

export const DraggableCustomPropertyRow = CustomPropertyRow;
